import { useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

export interface OrdersFirstRunEmptyProps {
  storeUrl: string;
}

export function OrdersFirstRunEmpty({ storeUrl }: OrdersFirstRunEmptyProps) {
  const t = useTranslations();
  const toast = useToast();

  async function handleShare() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ url: storeUrl });
      } catch {
        /* user dismissed the share sheet */
      }
      return;
    }
    try {
      await navigator.clipboard?.writeText(storeUrl);
      toast.success(t(keys.orders.linkCopied));
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <div className="flex flex-col items-center gap-3 rounded-[14px] border border-dashed border-[color:var(--border-strong)] bg-[color:var(--surface)] px-6 py-20 text-center">
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-[16px] bg-[color:var(--background-muted)] text-[color:var(--text-secondary)] shadow-sm"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M8 8h8M8 12h8M8 16h5" />
        </svg>
      </span>
      <div className="text-[17px] font-bold text-[color:var(--text-primary)]">
        {t(keys.orders.firstRunTitle)}
      </div>
      <div className="max-w-[380px] text-[13.5px] text-[color:var(--text-muted)]">
        {t(keys.orders.firstRunBody)}
      </div>
      <button
        type="button"
        onClick={handleShare}
        className="mt-2 inline-flex h-11 items-center gap-2 rounded-[11px] bg-[color:var(--accent-500)] px-4 text-[14px] font-bold text-[color:var(--on-accent)] shadow-sm transition-opacity hover:opacity-90"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
          <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
        </svg>
        {t(keys.orders.shareStoreLink)}
      </button>
      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[13px] font-semibold text-[color:var(--text-secondary)] hover:underline"
      >
        {t(keys.orders.viewMyStore)}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rtl:rotate-180">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </a>
    </div>
  );
}
